import { MdsDropdownButton, MdsDropdownItemKind } from "@/design-system/components/dropdown";
import { MdsSpacer } from "@/design-system/components/spacer";
import { mdsColors } from "@/design-system/foundations/colors";
import { Maybe } from "@/domains/common/types";
import { ZIndex } from "@/domains/design/constants";
import { css } from "@/domains/emotion";
import { copyTextToClipboard } from "@/modules/clipboard/copyTextToClipboard";
import { toastModule } from "@/modules/toast";
import { useAppStore } from "@/store";
import { CustomSyncErrorKind } from "@/store/sync/operations/errors/SyncError";
import { getOperationKindToClassMap } from "@/store/sync/operations/map";
import { SyncOperationKind } from "@/store/sync/types";
import { observer } from "mobx-react-lite";
import { MdsButton, MdsButtonSize, MdsButtonVariant } from "@/design-system/components/button";
import { clientEnvModule } from "@/modules/client-env";
import { DateTime } from "luxon";
import { useEffect, useRef, useState } from "react";

const customErrorKinds: Maybe<CustomSyncErrorKind>[] = [
  undefined,
  "INVALID",
  "PERMISSION_DENIED",
  "TRANSIENT",
  "UNKNOWN",
];

// Type for performance memory API
interface PerformanceMemory {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
}

interface PerformanceWithMemory extends Performance {
  memory?: PerformanceMemory;
}

// Performance monitoring sub-component
const PerformanceMetrics = observer(function PerformanceMetrics() {
  const [fps, setFps] = useState(0);
  const [memoryMB, setMemoryMB] = useState(0);
  const [frameTime, setFrameTime] = useState({ max: 0, avg: 0 });
  const frameCountRef = useRef(0);
  const lastTimeRef = useRef(performance.now());
  const frameTimes = useRef<number[]>([]);
  const lastUpdateRef = useRef(performance.now());

  useEffect(() => {
    let animationId: number;

    const measurePerformance = (currentTime: number) => {
      // Calculate FPS
      frameCountRef.current++;
      const deltaTime = currentTime - lastTimeRef.current;

      if (deltaTime >= 1000) {
        setFps(Math.round((frameCountRef.current * 1000) / deltaTime));
        frameCountRef.current = 0;
        lastTimeRef.current = currentTime;
      }

      // Continuously track frame times in background
      if (frameTimes.current.length > 0) {
        frameTimes.current.push(currentTime);

        // Keep only last 10 frames for lag detection
        if (frameTimes.current.length > 11) {
          // 11 timestamps = 10 frame deltas
          frameTimes.current.shift();
        }
      } else {
        frameTimes.current.push(currentTime);
      }

      // Update memory usage every 5 seconds
      if (currentTime - lastUpdateRef.current >= 5000) {
        const perfWithMemory = performance as PerformanceWithMemory;
        if (perfWithMemory.memory) {
          setMemoryMB(Math.round(perfWithMemory.memory.usedJSHeapSize / 1024 / 1024));
        }
        lastUpdateRef.current = currentTime;
      }

      animationId = requestAnimationFrame(measurePerformance);
    };

    // Add click listener to analyze recent frame times
    const handleClick = () => {
      if (frameTimes.current.length > 1) {
        // Calculate frame deltas from recent history
        const deltas = [];
        for (let i = 1; i < frameTimes.current.length; i++) {
          deltas.push(frameTimes.current[i] - frameTimes.current[i - 1]);
        }

        if (deltas.length > 0) {
          const maxFrameTime = Math.max(...deltas);
          const avgFrameTime = deltas.reduce((a, b) => a + b, 0) / deltas.length;

          setFrameTime({
            max: Math.round(maxFrameTime),
            avg: Math.round(avgFrameTime),
          });
        }
      }

      // Also update memory immediately on click
      const perfWithMemory = performance as PerformanceWithMemory;
      if (perfWithMemory.memory) {
        setMemoryMB(Math.round(perfWithMemory.memory.usedJSHeapSize / 1024 / 1024));
      }
    };

    document.addEventListener("click", handleClick, true);
    animationId = requestAnimationFrame(measurePerformance);

    return () => {
      cancelAnimationFrame(animationId);
      document.removeEventListener("click", handleClick, true);
    };
  }, []);

  return (
    <div className={performanceMetricsStyles}>
      <span>FPS: {fps.toString().padStart(2, " ")}</span>
      <span>Frame: {frameTime.max}ms</span>
      {<span>Mem: {memoryMB > 0 ? `${memoryMB}MB` : `...`}</span>}
    </div>
  );
});

// Debug content sub-component
const ExpandedDebugContent = observer(function ExpandedDebugContent() {
  const { store } = useAppStore();
  const { buildInfo } = clientEnvModule.buildInfo();

  const operationKinds: Maybe<SyncOperationKind>[] = [
    undefined,
    ...(Object.keys(getOperationKindToClassMap()) as SyncOperationKind[]),
  ];

  return (
    <div className={debugContentStyles}>
      <div className={mockApiErrorRowStyles}>
        <MdsSpacer />
        <MdsDropdownButton
          placement="below-right-alignment"
          iconKind={null}
          label={store.debug.operationsSubmitApiMockErrorKind ?? "disabled"}
          contentList={{
            items: customErrorKinds.map(kind => ({
              id: kind ?? "disabled",
              kind: MdsDropdownItemKind.Button,
              label: kind ?? "disabled",
              isChecked: store.debug.operationsSubmitApiMockErrorKind === kind,
              onClick: () => store.debug.setOperationsSubmitApiMockErrorKind(kind),
            })),
          }}
        />
        {"|"}
        <MdsDropdownButton
          placement="below-right-alignment"
          iconKind={null}
          label={store.debug.operationsSubmitApiMockErrorOperationKind ?? "all"}
          contentList={{
            items: operationKinds.map(kind => ({
              id: kind ?? "all",
              kind: MdsDropdownItemKind.Button,
              label: kind ?? "all",
              isChecked: store.debug.operationsSubmitApiMockErrorOperationKind === kind,
              onClick: () => store.debug.setOperationsSubmitApiMockErrorOperationKind(kind),
            })),
          }}
        />
      </div>
      <pre className={debugInfoStyles}>
        {JSON.stringify(store.sync.actionQueue.friendlyDebugInfo, null, 4)}
      </pre>
      {!!store.audioCapture.friendlyDebugInfo.length && (
        <pre className={debugInfoStyles}>
          {JSON.stringify(store.audioCapture.friendlyDebugInfo, null, 4)}
        </pre>
      )}
      <br />
      <pre className={debugInfoStyles}>
        <span>Commit: {buildInfo.gitCommitSha}</span>
        <br />
        <span>
          Build at:{" "}
          {buildInfo.buildTimestamp.toLocal().toLocaleString(DateTime.DATETIME_FULL_WITH_SECONDS)}
        </span>
      </pre>
    </div>
  );
});

/**
 * This is a debugging overlay which displays various pieces of info about local application state.
 * Most notably, it displays the current sync action queue, and allows for copying the queue's debug info to the clipboard.
 */
export const DebugInfoOverlay = observer(function DebugInfoOverlay() {
  const { store } = useAppStore();

  if (!store.debug.debugModeEnabled) {
    return null;
  }

  const isOpen = store.debug.debugModeToolbarExpanded;
  const toggleOpen = () => store.debug.toggleDebugModeToolbar();

  return (
    <>
      {/* Overlay content when expanded */}
      {isOpen && (
        <div className={overlayContentStyles}>
          <ExpandedDebugContent />
        </div>
      )}

      {/* Bottom bar - always visible */}
      <div className={bottomBarStyles(isOpen)}>
        <div className={headerStyles}>
          {!isOpen && (
            <>
              <PerformanceMetrics />
              <MdsButton
                onClick={toggleOpen}
                size={MdsButtonSize.XSmall}
                label="Expand"
                variant={MdsButtonVariant.Transparent}
              />
            </>
          )}
          {isOpen && (
            <>
              <PerformanceMetrics />
              <div className={headerButtonGroupStyles}>
                <MdsButton
                  label="Save to file"
                  size={MdsButtonSize.XSmall}
                  variant={MdsButtonVariant.Transparent}
                  onClick={() => store.sync.actionQueue.saveDebugInfoToFile()}
                />
                <MdsButton
                  onClick={async () => {
                    const fullDebugInfo = store.sync.actionQueue.fullDebugInfo;
                    await copyTextToClipboard({
                      text: JSON.stringify(fullDebugInfo, null, 4),
                    });
                    toastModule.triggerToast({ content: "Copied queue debug info to clipboard." });
                  }}
                  size={MdsButtonSize.XSmall}
                  label="Copy Info"
                  variant={MdsButtonVariant.Transparent}
                />
                <MdsButton
                  onClick={toggleOpen}
                  size={MdsButtonSize.XSmall}
                  label="Minimize"
                  variant={MdsButtonVariant.Transparent}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
});

// Updated styles
const bottomBarStyles = (isOpen: boolean) =>
  css({
    backgroundColor: mdsColors().white,
    border: `1px solid ${mdsColors().grey.x300}`,
    borderRadius: "4px",
    bottom: 0,
    padding: "4px 8px",
    position: "fixed",
    right: 0,
    fontSize: "14px",
    width: isOpen ? "600px" : "auto",
    height: "34px",
    zIndex: ZIndex.QueueDebugInfo,
    opacity: isOpen ? 1 : 0.8,
    transition: "all 0.2s ease",
  });

const overlayContentStyles = css({
  backgroundColor: mdsColors().white,
  border: `1px solid ${mdsColors().grey.x300}`,
  borderRadius: "4px",
  bottom: "42px", // Position above the bottom bar
  padding: "16px",
  position: "fixed",
  right: 0,
  fontSize: "14px",
  width: "600px",
  maxHeight: "calc(100vh - 100px)",
  overflow: "auto",
  zIndex: ZIndex.QueueDebugInfo - 1,
  boxShadow: "0 -2px 8px rgba(0, 0, 0, 0.1)",
});

const headerStyles = css({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  height: "100%",
  gap: "16px",
});

const headerButtonGroupStyles = css({
  display: "flex",
  alignItems: "center",
  gap: "4px",
});

const performanceMetricsStyles = css({
  display: "flex",
  gap: "12px",
  fontSize: "12px",
  fontFamily: "monospace",
  color: mdsColors().grey.x600,
  alignItems: "center",
  "& > span": {
    whiteSpace: "nowrap",
    "&:nth-of-type(1)": {
      width: "60px", // FPS: XX
    },
    "&:nth-of-type(2)": {
      width: "80px", // Frame: XXms
    },
    "&:nth-of-type(3)": {
      width: "80px", // Mem: XXXmb
    },
  },
});

const debugContentStyles = css({
  display: "flex",
  flexDirection: "column",
});

const mockApiErrorRowStyles = css({
  alignItems: "center",
  display: "flex",
  gap: "8px",
  marginBottom: "16px",
});

const debugInfoStyles = css({
  fontSize: "12px",
  overflow: "auto",
  maxHeight: "300px",
  backgroundColor: mdsColors().grey.x100,
  padding: "12px",
  borderRadius: "4px",
  fontFamily: "monospace",
});
